import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useIsLoggedInQuery } from '../../redux/apis/authApi';

import { MONTHS } from '../../utils/constant/global';
import { formattedAverage } from '../../utils/handler';
import { BT_BUTTON_LABELS } from '../../utils/constant/buttons';
import { LB_PERFORMANCE_MODULE } from '../../utils/constant/labels';

// import 'bootstrap/dist/css/bootstrap.min.css'; // Ensure Bootstrap CSS is imported

import PerformanceViewModel from './PerformanceViewModel';
import { useGetSixMonthPerformanceQuery } from '../../redux/apis/PerformanceApi';
import { setSelectedPerformance } from '../../redux/Slice/performanceSlice';

const { BTN_NEXT, BTN_PREVIOUS } = BT_BUTTON_LABELS;

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const WINDOW_SIZE = 6;
// Months are handled as a single index (year * 12 + month) so the window can't drift.
const EARLIEST_MONTH_KEY = 2024 * 12 + 7; // Aug 2024

const toMonthKey = (date) => date.getFullYear() * 12 + date.getMonth();

const fromMonthKey = (key) => ({ monthIndex: key % 12, year: Math.floor(key / 12) });

const formatDate = (key) => {
  const { monthIndex, year } = fromMonthKey(key);
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;
};

const getWindowMonths = (endKey) =>
  Array.from({ length: WINDOW_SIZE }, (_, i) => {
    const { monthIndex, year } = fromMonthKey(endKey - (WINDOW_SIZE - 1) + i);
    return { month: months[monthIndex], year };
  });

const getWindowRange = (endKey) => ({
  start: formatDate(endKey - (WINDOW_SIZE - 1)),
  end: formatDate(endKey + 1)
});

// eslint-disable-next-line no-unused-vars
const MonthPicker = ({ state, setState }) => {
  const dispatch = useDispatch();
  const latestKey = toMonthKey(new Date());
  const [endKey, setEndKey] = useState(latestKey);
  const { data } = useIsLoggedInQuery();

  const { data: performanceData, refetch: kpiPerformanceFetch } = useGetSixMonthPerformanceQuery({
    ...getWindowRange(endKey),
    userId: data?.data?._id
  });

  useEffect(() => {
    kpiPerformanceFetch();
  }, [endKey]);

  const canGoNext = endKey < latestKey;
  const canGoPrev = endKey - WINDOW_SIZE >= EARLIEST_MONTH_KEY;

  const handleNext = () => setEndKey((key) => Math.min(key + WINDOW_SIZE, latestKey));

  const handlePrev = () => setEndKey((key) => key - WINDOW_SIZE);

  const displayedMonths = getWindowMonths(endKey);

  const handleSelectedPerformance = (month, year) => {
    dispatch(
      setSelectedPerformance({
        selectedYear: year,
        selectedMonth: MONTHS[month],
        _id: data?.data?._id
      })
    );
    setState((prevState) => ({
      ...prevState,
      isModal: { model: true, submit: false },
      modalHeader: {
        title: 'View Performance'
      }
    }));
  };

  const first = displayedMonths[0];
  const last = displayedMonths[displayedMonths.length - 1];
  const rangeLabel =
    first && last
      ? `${first.month.slice(0, 3)} ${first.year} – ${last.month.slice(0, 3)} ${last.year}`
      : '';

  return (
    <div className="ep-performance">
      <div className="ep-performance__nav">
        <button
          type="button"
          className="ep-performance__nav-btn"
          disabled={!canGoPrev}
          onClick={handlePrev}
          aria-label={BTN_PREVIOUS}
          title={BTN_PREVIOUS}
        >
          <i className="icofont-rounded-left" aria-hidden />
        </button>
        <span className="ep-performance__range">{rangeLabel}</span>
        <button
          type="button"
          className="ep-performance__nav-btn"
          disabled={!canGoNext}
          onClick={handleNext}
          aria-label={BTN_NEXT}
          title={BTN_NEXT}
        >
          <i className="icofont-rounded-right" aria-hidden />
        </button>
      </div>
      <ul className="ep-performance__list">
        {displayedMonths.map(({ month, year }) => {
          const average =
            performanceData?.data?.find(
              (p) => p.month === months.indexOf(month) + 1 && p.year === year
            )?.average || 'N/A';
          const hasScore = average !== 'N/A';
          const tone = !hasScore ? 'empty' : average < 6.5 ? 'low' : average < 8 ? 'mid' : 'high';

          return (
            <li key={`${month}-${year}`} className="ep-performance__row">
              <span className="ep-performance__month">
                {month} <span className="ep-performance__year">{year}</span>
              </span>
              <button
                type="button"
                className={`ep-performance__score ep-performance__score--${tone}`}
                disabled={!hasScore}
                onClick={() => handleSelectedPerformance(months.indexOf(month), year)}
                title={hasScore ? 'View performance' : 'No rating yet'}
              >
                {hasScore ? formattedAverage(average) : 'N/A'}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const initialState = {
  isModal: { model: false, submit: false },
  modalHeader: { title: '', isEdit: false, editId: '' }
};

function PerformanceCard() {
  const [state, setState] = useState(initialState);

  return (
    <div className="card h-100 ep-info-card">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <div className="ep-info-card__title">
          <span className="ep-info-card__title-icon" aria-hidden>
            <i className="bi bi-graph-up" />
          </span>
          <h6 className="mb-0 fw-bold">{LB_PERFORMANCE_MODULE.LB_PERFORMANCE_TITLE}</h6>
        </div>
      </div>
      <MonthPicker state={state} setState={setState} />
      <PerformanceViewModel state={state} setState={setState} />
    </div>
  );
}

export default PerformanceCard;
